export class ApiError extends Error {
  constructor(public code: string, public status: number, public details?: unknown) {
    super(code);
    this.name = "ApiError";
  }
}

export const ERROR_MESSAGES: Record<string, string> = {
  invalid_credentials: "Email or password is incorrect.",
  rate_limited: "Too many attempts. Please try again later.",
  weak_password: "Password is too weak. Use at least 12 characters, with letters and digits.",
  invalid_token: "This link is invalid or expired. Ask the inviter to resend.",
  etag_mismatch: "Someone else updated this. Reload to see the latest version.",
  last_super_admin: "You can't do this — you would leave the platform without a super admin.",
  forbidden: "You don't have access to this resource.",
  not_found: "Resource not found.",
  icon_too_large: "Icon must be 50KB or smaller.",
  already_member: "This user is already a member of the project.",
  csrf_invalid: "Your session expired. Please sign in again.",
  unauthenticated: "Please sign in to continue.",
  invalid_current: "Current password is incorrect.",
  cannot_disable_self: "You can't disable your own account.",
  invalid_name: "Name must be between 2 and 80 characters.",
  body_too_large: "Request too large.",

  // Additional codes emitted by the backend (gap-audit Errors section).
  bad_game_id: "Invalid project ID.",
  bad_id: "Invalid ID.",
  bad_json: "Invalid request format.",
  bad_user_id: "Invalid user ID.",
  internal: "Something went wrong on our side. Please try again.",
  invalid_email: "Enter a valid email address.",
  mail_send_failed:
    "Couldn't send the invitation email. Check the SMTP settings and try again.",
  invalid_role: "Unknown role.",
  missing_game_id: "Project not specified.",
  precondition_required: "Missing precondition header. Reload and try again.",
  forbidden_origin: "Request blocked for security. Reload and try again.",
  bad_player_id: "Invalid player ID.",
  stats_not_object: "Stats must be a JSON object.",
  nothing_to_update: "No changes to save.",
  display_name_invalid: "Display name must be between 1 and 80 characters.",
  avatar_not_allowed: "That avatar is not in the project's allowed list.",
  invalid_stats: "Stats do not match the project's schema.",
  invalid_meta: "Meta does not match the project's schema.",
  meta_not_object: "Meta must be a JSON object.",

  // Phase 3.2 — guild & chat moderation codes.
  bad_guild_id: "Invalid guild ID.",
  bad_message_id: "Invalid message ID.",
  name_invalid: "Guild name must be between 1 and 64 characters.",
  description_invalid: "Description must be 512 characters or fewer.",
  visibility_invalid: "Visibility must be Open or Invite only.",
  role_invalid: "Unknown member role.",
  message_invalid: "Message is empty or too long.",
  invalid_role_transition: "That role change isn't possible — reload to see the current roles.",
  cannot_kick_leader: "You can't kick the guild leader. Transfer leadership first.",

  // Phase 5 — leaderboards codes.
  leaderboard_not_found: "Leaderboard not found",
  leaderboard_validation: "Invalid leaderboard configuration",
  leaderboard_score_not_found: "Score not found in this cycle",
  leaderboard_cycle_archived: "This cycle is archived; cannot modify",
  leaderboard_subscribe_denied: "Leaderboard does not exist or is in a different game",
  leaderboard_realtime_disabled: "Realtime is disabled for this leaderboard",
};

export function describeError(code: string): string {
  return ERROR_MESSAGES[code] ?? "Something went wrong. Please try again.";
}
